import React from "react";
import { ChevronRight } from "lucide-react";
import { formatDate } from "../../utils/calendar.mjs";
export function DailyCard({ title, action, children, className = "", embedded = false }) {
  return (
    <section className={`${embedded ? "" : "dashboardCard"} ${className}`}>
      <div className="cardHeading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
export const goalLabels = {
  callResults: "Résultats d’appels saisis",
  decisionMakersReached: "Décideurs joints",
  needsDetected: "Besoins détectés",
  quotesSent: "Devis envoyés",
};
export function DailyGoals({ goals, actual, onEdit, embedded = false }) {
  return (
    <DailyCard
      embedded={embedded}
      title="Mes objectifs du jour"
      action={
        onEdit ? (
          <button className="textAction" onClick={onEdit}>Définir mes objectifs</button>
        ) : null
      }
    >
      <div className="dailyGoalGrid">
        {Object.entries(goalLabels).map(([key, label]) => (
          <div className="dailyGoal" key={key} data-testid={`goal-${key}`}>
            <span>{label}</span>
            <strong>
              {actual[key]} / {goals[key]}
            </strong>
            <progress
              aria-label={label}
              value={Math.min(actual[key], goals[key])}
              max={goals[key]}
            />
            <small>{Math.round((actual[key] / goals[key]) * 100)} %</small>
          </div>
        ))}
      </div>
    </DailyCard>
  );
}
export function DailyPace({ pace, isToday, workday }) {
  return (
    <DailyCard title="Rythme du jour">
      {isToday ? (
        <>
          <strong className="paceValue">
            {pace.actual} réalisés · {pace.expected} attendus à {pace.clock}
          </strong>
          <p className={pace.difference < 0 ? "paceBehind" : "paceOnTrack"}>
            {pace.difference < 0
              ? `${-pace.difference} résultats sous le rythme prévu.`
              : pace.difference > 0
                ? `+${pace.difference} résultats d’avance sur votre rythme.`
                : "Vous êtes dans le rythme."}
          </p>
        </>
      ) : (
        <p>
          Le rythme horaire est disponible pour aujourd’hui. Les résultats de la
          date choisie restent consultables.
        </p>
      )}
      <small>
        Journée : {workday.start}–{workday.end}
        {workday.lunchStart && workday.lunchEnd
          ? ` · Pause : ${workday.lunchStart}–${workday.lunchEnd}`
          : ""}
      </small>
      <p className="hint">
        Les résultats validés sont comptés. Les appels sont effectués sur un
        téléphone externe.
      </p>
    </DailyCard>
  );
}
export function NextBestAction({
  action,
  onOpen,
  title = "🔥 Prochaine meilleure action",
}) {
  return (
    <DailyCard title={title} className="nextBestAction">
      {action ? (
        <>
          <div className="nextBestHeading">
            <div>
              <h3>{action.client.name}</h3>
              <p>Action recommandée : {action.title}</p>
            </div>
            <strong className="scorePill">Priorité {action.score}/100</strong>
          </div>
          <p className="recommendationWhy">
            <b>
              {action.absolute
                ? "Échéance prioritaire"
                : "Priorité commerciale"}
            </b>{" "}
            · {action.whyNow}
          </p>
          {action.signal && (
            <div className="nextSignal">
              <b>
                {action.signal.qty ? `${action.signal.qty} postes · ` : ""}
                {action.signal.job || action.signal.content}
              </b>
              <small>
                Source : {action.signal.source || "Non renseignée"} · Signal du{" "}
                {formatDate(action.signal.date)}
              </small>
            </div>
          )}
          {!action.signal && (
            <p className="hint">
              Source : aucun signal renseigné · Date du signal : non renseignée.
            </p>
          )}
          <div className="recommendationDates">
            <p>
              <b>Dernière interaction :</b>{" "}
              {action.lastInteraction
                ? `${new Date(action.lastInteraction.at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })} · ${action.lastInteraction.label}`
                : "Aucun échange commercial renseigné"}
            </p>
            <p>
              <b>Prochaine action :</b> {formatDate(action.recommendedDate)}{" "}
              {action.task?.time || ""} · {action.title}
            </p>
            {action.nextActionDate &&
              action.nextActionDate > action.recommendedDate && (
                <p>
                  Suivi déjà programmé, conservé au{" "}
                  {formatDate(action.nextActionDate)}.
                </p>
              )}
          </div>
          <details open>
            <summary>Pourquoi maintenant ?</summary>
            <ul className="nextReasons">
              {action.reasons.map((r, i) => (
                <li key={i}>
                  {r.points > 0 ? "+" : ""}
                  {r.points} · {r.label}
                </li>
              ))}
            </ul>
          </details>
          <button className="primary" onClick={() => onOpen(action)}>
            {action.label} <ChevronRight />
          </button>
          {action.reasons.reduce((sum, r) => sum + r.points, 0) > 100 && (
            <small className="hint">
              Le total des contributions est plafonné à 100.
            </small>
          )}
          {action.reasons.reduce((sum, r) => sum + r.points, 0) < 0 && (
            <small className="hint">
              Le total des contributions est ramené à 0.
            </small>
          )}
        </>
      ) : (
        <p className="dashboardEmpty">
          Les priorités du jour sont traitées. Les prochains suivis restent dans
          l’agenda ; vous pouvez rechercher un autre établissement.
        </p>
      )}
    </DailyCard>
  );
}
export function HotOpportunities({ actions, onOpen }) {
  return (
    <DailyCard title="🔥 Opportunités chaudes">
      {actions.slice(0, 5).map((a) => (
        <button
          className="dailyOpportunity hotOpportunity"
          key={a.client.id}
          onClick={() => onOpen(a)}
        >
          <div className="hotOpportunityText">
            <b className="hotOpportunityName">{a.client.name}</b>
            <small className="hotOpportunityIdentity">
              {[a.client.city, a.client.kind !== "prospect" ? "Client JM INTERIM" : "Prospect"].filter(Boolean).join(" · ")}
            </small>
            {a.signal?.job && (
              <span className="hotOpportunityNeed">
                {a.signal.qty && !/^\d/.test(a.signal.job) ? `${a.signal.qty} poste${Number(a.signal.qty) > 1 ? "s" : ""} · ` : ""}
                {a.signal.job}
              </span>
            )}
            <span className="hotOpportunityAction">{a.title}</span>
          </div>
          <strong className="hotOpportunityScore">{a.score}<small>/100</small></strong>
        </button>
      ))}
      {!actions.length && (
        <p className="dashboardEmpty">
          Aucune opportunité chaude à traiter aujourd’hui.
        </p>
      )}
    </DailyCard>
  );
}
export function Commitments({ items, state, onTask, onComplete }) {
  return (
    <DailyCard title={`🤝 Mes engagements aujourd’hui (${items.length})`}>
      {items.map((e) => {
        const task = state.tasks.find((t) => t.id === e.taskId);
        return (
          <div className="dailyCommitment" key={e.id}>
            <b>{e.title}</b>
            <small>
              {state.clients.find((c) => c.id === e.clientId)?.name} ·{" "}
              {formatDate(e.due)} {task?.time || ""}
            </small>
            <div>
              <button
                className="secondary"
                onClick={() => onTask(task)}
                disabled={!task}
              >
                Ouvrir
              </button>
              <button
                className="textAction"
                onClick={() => onComplete(e.taskId)}
                disabled={!task}
              >
                Terminer
              </button>
            </div>
          </div>
        );
      })}
      {!items.length && (
        <p className="dashboardEmpty">Aucun engagement à tenir à cette date.</p>
      )}
    </DailyCard>
  );
}
export function ActionAlerts({ alerts, onOpen }) {
  return (
    <DailyCard title="⚠ À ne pas oublier">
      {alerts.map((a) => (
        <button className="dailyAlert" key={a.key} onClick={() => onOpen(a)}>
          <b>{a.items.length}</b>
          <span>{a.label}</span>
          <ChevronRight />
        </button>
      ))}
      {!alerts.length && (
        <p className="dashboardEmpty">Aucune alerte nécessitant une action.</p>
      )}
    </DailyCard>
  );
}
export function WeeklyIndicators({ actual, previous, period }) {
  const valueOf = (source, key) => {
    const value = Number(source?.[key]);
    return Number.isFinite(value) ? value : 0;
  };
  const groups = [
    [
      "Activité",
      [
        ["callResults", "Résultats d’appels saisis"],
        ["clientVisits", "Visites clients"],
      ],
    ],
    [
      "Résultats",
      [
        ["needsDetected", "Besoins détectés"],
        ["quotesSent", "Devis envoyés"],
        ["quotesSigned", "Devis signés"],
        ["clientsReactivated", "Clients réactivés"],
      ],
    ],
  ];
  return (
    <DailyCard title="Mes indicateurs de la semaine">
      {period && <p className="hint">Du {formatDate(period.from)} au {formatDate(period.to)} · Données enregistrées pour votre compte et votre agence.</p>}
      {groups.map(([title, rows]) => (
        <div key={title}>
          <h3 className="indicatorGroup">{title}</h3>
          <div className="dailyWeekMetrics">
            {rows.map(([key, label]) => {
              const current = valueOf(actual, key);
              const difference = current - valueOf(previous, key);
              return (
                <div key={key}>
                  <strong>{current}</strong>
                  <span>{label}</span>
                  <small>
                    {difference > 0 ? "↑ +" : difference < 0 ? "↓ " : "="}
                    {difference} vs semaine précédente
                  </small>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </DailyCard>
  );
}
const money = (value) =>
  value === null ? "Non renseigné" : `${value.toLocaleString("fr-FR")} €`;
export function CommercialPotential({ potential }) {
  return (
    <DailyCard title="Potentiel en cours">
      <div className="potentialMetrics">
        <div>
          <strong>{potential.activeCount}</strong>
          <span>Opportunités actives</span>
        </div>
        <div>
          <strong>{money(potential.estimatedCA)}</strong>
          <span>CA potentiel estimé</span>
        </div>
        <div>
          <strong>{money(potential.quotesInProgress)}</strong>
          <span>Devis en cours</span>
        </div>
        <div>
          <strong>{money(potential.recoveredCA)}</strong>
          <span>CA réactivé ce mois</span>
        </div>
      </div>
      <p className="hint">
        Montants renseignés dans les données locales. Une seule version de devis
        par besoin est retenue ; aucune estimation de tarif automatique.
      </p>
    </DailyCard>
  );
}
export function PersonalStreak({ regularity }) {
  return (
    <DailyCard title="Ma dynamique personnelle">
      <p>
        <b>
          {regularity.currentStreak} jour
          {regularity.currentStreak > 1 ? "s" : ""} ouvré
          {regularity.currentStreak > 1 ? "s" : ""} consécutif
          {regularity.currentStreak > 1 ? "s" : ""}
        </b>{" "}
        avec l’objectif de résultats atteint.
      </p>
      <p>
        Objectif atteint{" "}
        <b>
          {regularity.weekCount} jour{regularity.weekCount > 1 ? "s" : ""}
        </b>{" "}
        cette semaine.
      </p>
      <small>
        Meilleure série : {regularity.bestStreak} jours ouvrés. Suivi personnel,
        sans classement.
      </small>
    </DailyCard>
  );
}
export function CommercialPipeline({ stages, state, onSelect }) {
  return (
    <DailyCard title="Mon pipeline commercial" className="commercialPipelineCard">
      <div className="dashboardPipeline">
        {stages.map((stage, i) => (
          <button
            key={stage.label}
            onClick={() =>
              onSelect({
                title: stage.label,
                items: stage.clients,
                kind: "clients",
              })
            }
          >
            <span>{stage.label}</span>
            <div className={`stageBar stage${i}`}>
              <i
                style={{
                  width: `${state.clients.length ? (stage.clients.length / state.clients.length) * 100 : 0}%`,
                }}
              />
            </div>
            <b>{stage.clients.length}</b>
          </button>
        ))}
      </div>
      <small className="pipelineHelp">
        Établissements répartis selon leur étape la plus avancée cette semaine
        (remise à zéro chaque lundi à minuit). Cliquez sur une étape pour
        consulter les établissements concernés.
      </small>
      <div className="contractMix">
        {["Intérim", "Vacation"].map((type) => (
          <button
            key={type}
            onClick={() =>
              onSelect({
                title: `${type} · Candidats sous contrat cette semaine`,
                items: state.weeklyCandidates?.[type] || [],
                kind: "candidates",
              })
            }
          >
            <span>{type}</span>
            <b>{state.weeklyCandidates ? (state.weeklyCandidates[type] || []).length : "—"}</b>
          </button>
        ))}
      </div>
      <small className="pipelineHelp">Candidats distincts sous contrat cette semaine, du lundi au dimanche. Plusieurs contrats pour une même personne comptent pour un candidat par type.</small>
    </DailyCard>
  );
}
